import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { standGet, standPost } from "./standApi";
import "./stand.css";

// The Stand: the talk on each page of a paper. Opens beside the reader; a bottom sheet on phones.
// Data: GET stand/edition/:id?page=&sort=, POST stand/edition/:id, stand/comment/:id/reply|react, stand/report, stand/remove, stand/me.
const LABEL = { point: "Point!", abeg: "Abeg", laugh: "😂", wahala: "Wahala", pray: "🙏" };
const HINT = { point: "You have a point, even if I disagree", abeg: "No way", laugh: "This one funny", wahala: "Trouble dey", pray: "Thinking of those affected" };

const ago = (iso) => {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "now";
  if (mins < 60) return `${mins} min`;
  if (mins < 1440) return `${Math.round(mins / 60)} hr`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

function Who({ line }) {
  return (
    <div className="st-who">
      <span className="st-av" aria-hidden="true">{line.initials}</span>
      <b>{line.mine ? "You" : line.name}</b>
      <span className="st-rank">{line.title}</span>
      {line.readIt ? <span className="st-tick" title="This reader opened the paper">✓ Read it</span> : null}
      {line.held ? <span className="st-held">Only you can see this</span> : null}
      <span className="st-when">{ago(line.at)}</span>
    </div>
  );
}

function SmallActions({ line, kind, onReply, onReport, onRemove }) {
  return (
    <div className="st-acts">
      {onReply ? <button type="button" onClick={() => onReply(line)}>Reply</button> : null}
      {line.mine
        ? <button type="button" onClick={() => onRemove(kind, line)}>Remove</button>
        : <button type="button" onClick={() => onReport(kind, line)}>Report</button>}
    </div>
  );
}

export default function StandPanel({ editionId, page, onClose, onCount }) {
  const [viewPage, setViewPage] = useState(null); // null = follow the page being read; "paper" = whole paper; number = a chosen page
  const [story, setStory] = useState(0);
  const [sort, setSort] = useState("hot");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [problem, setProblem] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const [naming, setNaming] = useState(null); // { name, error } while choosing a Stand name
  const [settings, setSettings] = useState(false);
  const input = useRef(null);
  const scroller = useRef(null);
  const panel = useRef(null);
  const shown = viewPage === null ? page : viewPage;

  const load = useCallback(async () => {
    const q = shown && shown !== "paper" ? `page=${shown}&story=${story}&` : "";
    const r = await standGet(`stand/edition/${editionId}?${q}sort=${sort}`);
    if (r.ok) { setData(r.data); setError(""); } else setError(r.error);
  }, [editionId, shown, story, sort]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setStory(0); }, [shown]);
  useEffect(() => { setReplyTo(null); setProblem(""); }, [shown, story]);
  // New lines arrive while the panel is open (every 20 seconds, only while the tab is in view).
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) load(); }, 20000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  useEffect(() => { if (!toast) return undefined; const t = setTimeout(() => setToast(""), 2600); return () => clearTimeout(t); }, [toast]);

  // Phones cover the page with the keyboard without moving fixed panels, so fit the panel to what is still visible.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const fit = () => {
      const el = panel.current;
      if (!el) return;
      const keyboard = vv.scale <= 1.01 && window.innerHeight - vv.height > 120;
      el.classList.toggle("st-kb", keyboard);
      el.style.top = keyboard ? `${vv.offsetTop}px` : "";
      el.style.height = keyboard ? `${vv.height}px` : "";
    };
    fit();
    vv.addEventListener("resize", fit);
    vv.addEventListener("scroll", fit);
    return () => {
      vv.removeEventListener("resize", fit);
      vv.removeEventListener("scroll", fit);
    };
  }, []);

  const refreshCount = async () => {
    const r = await standGet(`stand/status/${editionId}`);
    if (r.ok && onCount) onCount(r.data.count || 0);
  };

  const send = async (nameJustSet) => {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true); setProblem("");
    const body = { message: text, page: shown && shown !== "paper" ? shown : null, story };
    const r = replyTo ? await standPost(`stand/comment/${replyTo.id}/reply`, body) : await standPost(`stand/edition/${editionId}`, body);
    setBusy(false);
    if (!r.ok) {
      if (r.data && r.data.needName && !nameJustSet) {
        setNaming({ name: (data && data.me && data.me.suggested) || "", error: "" });
        return;
      }
      setProblem(r.error);
      return;
    }
    setDraft(""); setReplyTo(null);
    setToast(r.data.held ? (r.data.note || "A person will check this quickly.") : "Posted. Now the Stand can answer you.");
    await load();
    refreshCount();
    if (scroller.current) scroller.current.scrollTop = 0;
  };

  const saveName = async (e) => {
    e.preventDefault();
    const r = await standPost("stand/me", { name: naming.name });
    if (!r.ok) { setNaming({ ...naming, error: r.error }); return; }
    setNaming(null);
    await load();
    if (draft.trim()) send(true);
  };

  const react = async (line, kind) => {
    const r = await standPost(`stand/comment/${line.id}/react`, { kind });
    if (!r.ok) { setToast(r.error); return; }
    setData((d) => ({ ...d, items: d.items.map((x) => (x.id === line.id ? { ...x, rx: r.data.rx || {}, myRx: r.data.myRx || null } : x)) }));
  };
  const report = async (kind, line) => {
    const r = await standPost("stand/report", { kind, id: line.id });
    setToast(r.ok ? "Thanks. A moderator will look at this line." : r.error);
  };
  const remove = async (kind, line) => {
    const r = await standPost("stand/remove", { kind, id: line.id });
    if (r.ok) { setToast("Removed."); await load(); refreshCount(); } else setToast(r.error);
  };
  const startReply = (line) => { setReplyTo(line); setProblem(""); if (input.current) input.current.focus(); };
  const toggleTick = async () => {
    const hide = !(data.me && data.me.hideTick);
    const r = await standPost("stand/me", { hideTick: hide });
    if (r.ok) { setData((d) => ({ ...d, me: { ...d.me, hideTick: hide } })); load(); } else setToast(r.error);
  };

  const t = data && data.thread;
  const onThisPage = shown && shown !== "paper";
  const remaining = (data ? data.maxLength : 280) - draft.length;

  // Rendered on the page body so no older floating button or layer can sit on top of it.
  return createPortal(
    <>
      <button type="button" className="st-scrim" aria-label="Close the Stand" onClick={onClose} />
      <aside className="st-panel" ref={panel} aria-label="The Stand">
        <header className="st-head">
          <div className="st-title">
            <h2>The Stand</h2>
            <div className="st-head-btns">
              {data && data.me && data.me.name ? <button type="button" className="st-link" aria-expanded={settings} onClick={() => setSettings(!settings)}>{data.me.name}</button> : null}
              <button type="button" className="st-close" onClick={onClose} aria-label="Close">×</button>
            </div>
          </div>
          {settings && data && data.me ? (
            <div className="st-settings">
              <label><input type="checkbox" checked={!data.me.hideTick} onChange={toggleTick} /> Show my “Read it” tick</label>
              <small>You post as {data.me.name}. Your email and photo are never shown.</small>
            </div>
          ) : null}
          <div className="st-chips" role="tablist" aria-label="Which talk">
            {onThisPage && t && t.page === shown ? (t.stories.length ? t.stories : [{ i: 0, headline: `Page ${shown}`, n: 0 }]).map((x) => (
              <button type="button" role="tab" aria-selected={t.story === x.i} key={`s${x.i}`} className={t.story === x.i ? "on" : ""} onClick={() => setStory(x.i)}>
                {x.headline}{x.n ? ` · ${x.n}` : ""}
              </button>
            )) : null}
            <button type="button" role="tab" aria-selected={shown === "paper" || !shown} className={shown === "paper" || !shown ? "on paper" : "paper"} onClick={() => setViewPage("paper")}>
              Whole paper{data && data.paperCount ? ` · ${data.paperCount}` : ""}
            </button>
            {viewPage !== null && page ? <button type="button" role="tab" aria-selected="false" className="paper" onClick={() => setViewPage(null)}>Back to this page</button> : null}
            {data ? data.talkedPages.filter((p) => p.page !== page).map((p) => (
              <button type="button" role="tab" aria-selected={viewPage === p.page} key={p.page} className={viewPage === p.page ? "on" : ""} onClick={() => setViewPage(p.page)}>
                p. {p.page}: {p.headline} · {p.n}
              </button>
            )) : null}
          </div>
        </header>

        <div className="st-scroll" ref={scroller}>
          {error ? <p className="st-error">{error} <button type="button" className="st-link" onClick={load}>Try again</button></p> : null}
          {!data && !error ? <p className="st-empty">Loading the Stand…</p> : null}
          {t ? (
            <div className="st-host">
              <small>{t.serious ? "SERIOUS STORY · 😂 IS OFF" : (t.page ? `PAGE ${t.page}` : "THE WHOLE PAPER")}</small>
              {t.page && t.stories.length ? <strong>{t.headline}</strong> : null}
              <p>{t.question}</p>
              <span className="st-host-tag">Asked by the Stand host</span>
              {onThisPage && viewPage !== null ? <button type="button" className="st-go" onClick={() => window.dispatchEvent(new CustomEvent("tns:reader-go", { detail: shown }))}>Go to page {shown}</button> : null}
            </div>
          ) : null}
          {data && data.calm ? <p className="st-calm">The Stand is in calm mode. A person checks each line before others see it.</p> : null}
          {t && t.closed ? <p className="st-calm">{t.closedWhy}</p> : null}
          {data && data.items.length ? (
            <div className="st-sort">
              <span>{data.items.length} {data.items.length === 1 ? "line" : "lines"}</span>
              <span className="st-seg">
                <button type="button" className={sort === "hot" ? "on" : ""} aria-pressed={sort === "hot"} onClick={() => setSort("hot")}>Hottest</button>
                <button type="button" className={sort === "new" ? "on" : ""} aria-pressed={sort === "new"} onClick={() => setSort("new")}>Newest</button>
              </span>
            </div>
          ) : null}
          {data && !data.items.length && t && !t.closed ? <p className="st-empty">No one has talked here yet. First line takes the stand.</p> : null}
          {data ? data.items.map((line) => (
            <article className="st-line" key={line.id}>
              <Who line={line} />
              <p className="st-text">{line.text}</p>
              {!line.held ? (
                <div className="st-rx">
                  {t.kinds.map((k) => (
                    <button type="button" key={k} title={HINT[k]} disabled={line.mine} className={line.myRx === k ? "on" : ""} aria-pressed={line.myRx === k} onClick={() => react(line, k)}>
                      {LABEL[k]} {(line.rx && line.rx[k]) || 0}
                    </button>
                  ))}
                </div>
              ) : null}
              <SmallActions line={line} kind="comment" onReply={t.closed || line.held ? null : startReply} onReport={report} onRemove={remove} />
              {line.replies.length ? (
                <div className="st-replies">
                  {line.replies.map((r) => (
                    <div key={r.id}>
                      <Who line={r} />
                      <p className="st-text small">{r.text}</p>
                      <SmallActions line={r} kind="reply" onReport={report} onRemove={remove} />
                    </div>
                  ))}
                </div>
              ) : null}
            </article>
          )) : null}
        </div>

        {t && !t.closed ? (
          <footer className="st-compose">
            {naming ? (
              <form onSubmit={saveName} className="st-name">
                <label htmlFor="st-name-input"><b>Choose your Stand name</b><small>This is how others see you. You can change it once every 30 days.</small></label>
                <div className="st-row">
                  <input id="st-name-input" value={naming.name} maxLength={24} onChange={(e) => setNaming({ ...naming, name: e.target.value, error: "" })} autoFocus />
                  <button type="submit" className="st-send">Save and post</button>
                </div>
                {naming.error ? <p className="st-problem">{naming.error}</p> : null}
                <button type="button" className="st-link" onClick={() => setNaming(null)}>Not now</button>
              </form>
            ) : (
              <>
                {replyTo ? (
                  <div className="st-replying"><span>Replying to {replyTo.mine ? "yourself" : replyTo.name}</span><button type="button" className="st-link" onClick={() => setReplyTo(null)}>Cancel</button></div>
                ) : (
                  <div className="st-starters">
                    {t.starters.map((s) => <button type="button" key={s} onClick={() => { setDraft((d) => (d ? `${d} ${s}` : s)); if (input.current) input.current.focus(); }}>{s}</button>)}
                  </div>
                )}
                <div className="st-row">
                  <label htmlFor="st-draft" className="st-sr">Your line</label>
                  <textarea id="st-draft" ref={input} rows={1} value={draft} maxLength={(data ? data.maxLength : 280) + 20} placeholder={replyTo ? "Answer am…" : "Wetin you think?"}
                    onChange={(e) => { setDraft(e.target.value); setProblem(""); }}
                    onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") send(); }} />
                  <button type="button" className="st-send" disabled={busy || !draft.trim() || remaining < 0} onClick={() => send()}>{busy ? "…" : "Post"}</button>
                </div>
                <div className="st-foot"><span className="st-problem" role="alert">{problem}</span><span className={remaining < 0 ? "st-over" : "st-count"}>{draft.length}/{data ? data.maxLength : 280}</span></div>
              </>
            )}
          </footer>
        ) : null}
        {toast ? <div className="st-toast" role="status">{toast}</div> : null}
      </aside>
    </>,
    document.body
  );
}
